import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  IconButton,
  InputAdornment,
  CircularProgress,
  Fade,
  Alert,
  useTheme,
  alpha,
  Chip,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  AdminPanelSettings,
  Security,
  Shield,
  VpnKey,
  VerifiedUser,
  Fingerprint,
  Lock,
  Warning,
} from '@mui/icons-material';
import { useAuth } from '../../hooks';

// Premium Super Admin theme - Dark Developer
const superAdminTheme = {
  primary: '#0F172A',
  secondary: '#1E293B',
  accent: '#38BDF8',
  highlight: '#0EA5E9',
  terminal: '#10B981',
  warning: '#F59E0B',
  dark: '#020617',
  light: '#94A3B8',
  gradient1: 'linear-gradient(135deg, #020617 0%, #0F172A 25%, #1E293B 75%, #334155 100%)',
  gradient2:
    'radial-gradient(circle at 25% 25%, rgba(56, 189, 248, 0.15) 0%, transparent 50%), radial-gradient(circle at 75% 75%, rgba(16, 185, 129, 0.1) 0%, transparent 50%)',
  matrix:
    'linear-gradient(0deg, transparent 24%, rgba(16, 185, 129, 0.05) 25%, rgba(16, 185, 129, 0.05) 26%, transparent 27%, transparent 74%, rgba(16, 185, 129, 0.05) 75%, rgba(16, 185, 129, 0.05) 76%, transparent 77%, transparent)',
};

const SuperAdminLogin = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { signIn, isLoading, hasError, error: authError } = useAuth();
  const containerRef = useRef(null);
  const canvasRef = useRef(null);

  const [formData, setFormData] = useState({
    username: '',
    password: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [animateElements, setAnimateElements] = useState(false);

  // Authority network effect
  useEffect(() => {
    setAnimateElements(true);

    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;

    // Network nodes representing system control points
    const nodes = [];
    const connections = [];
    const nodeCount = 25;

    class Node {
      constructor(isCore = false) {
        this.x = Math.random() * canvas.width;
        this.y = Math.random() * canvas.height;
        this.vx = (Math.random() - 0.5) * 0.2;
        this.vy = (Math.random() - 0.5) * 0.2;
        this.radius = isCore ? 8 : 4;
        this.isCore = isCore;
        this.pulsePhase = Math.random() * Math.PI * 2;
        this.connections = [];
      }

      update() {
        this.x += this.vx;
        this.y += this.vy;

        if (this.x < 50 || this.x > canvas.width - 50) this.vx *= -1;
        if (this.y < 50 || this.y > canvas.height - 50) this.vy *= -1;

        this.pulsePhase += 0.02;
      }

      draw() {
        // Outer glow
        const gradient = ctx.createRadialGradient(
          this.x,
          this.y,
          0,
          this.x,
          this.y,
          this.radius * 3
        );
        gradient.addColorStop(0, this.isCore ? superAdminTheme.accent : superAdminTheme.terminal);
        gradient.addColorStop(1, 'transparent');

        ctx.fillStyle = gradient;
        ctx.globalAlpha = 0.3 + Math.sin(this.pulsePhase) * 0.2;
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.radius * 3, 0, Math.PI * 2);
        ctx.fill();

        // Core
        ctx.globalAlpha = 1;
        ctx.fillStyle = this.isCore ? superAdminTheme.accent : superAdminTheme.terminal;
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.radius, 0, Math.PI * 2);
        ctx.fill();

        // Inner bright spot
        ctx.fillStyle = 'rgba(255, 255, 255, 0.8)';
        ctx.beginPath();
        ctx.arc(
          this.x - this.radius * 0.3,
          this.y - this.radius * 0.3,
          this.radius * 0.3,
          0,
          Math.PI * 2
        );
        ctx.fill();
      }
    }

    // Create nodes - 5 core authority nodes and rest are regular
    for (let i = 0; i < 5; i++) {
      nodes.push(new Node(true));
    }
    for (let i = 0; i < nodeCount - 5; i++) {
      nodes.push(new Node(false));
    }

    // Data flow particles
    const particles = [];
    class DataParticle {
      constructor(startNode, endNode) {
        this.start = startNode;
        this.end = endNode;
        this.progress = 0;
        this.speed = 0.01 + Math.random() * 0.02;
      }

      update() {
        this.progress += this.speed;
        if (this.progress >= 1) {
          this.progress = 0;
          // Randomly switch direction
          if (Math.random() > 0.5) {
            const temp = this.start;
            this.start = this.end;
            this.end = temp;
          }
        }
      }

      draw() {
        const x = this.start.x + (this.end.x - this.start.x) * this.progress;
        const y = this.start.y + (this.end.y - this.start.y) * this.progress;

        ctx.fillStyle = superAdminTheme.accent;
        ctx.globalAlpha = 1 - Math.abs(this.progress - 0.5) * 2;
        ctx.beginPath();
        ctx.arc(x, y, 2, 0, Math.PI * 2);
        ctx.fill();
      }
    }

    const animate = () => {
      ctx.fillStyle = 'rgba(2, 6, 23, 0.05)';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      ctx.globalAlpha = 1;

      // Update nodes
      nodes.forEach((node) => node.update());

      // Draw connections
      nodes.forEach((node, i) => {
        nodes.slice(i + 1).forEach((otherNode) => {
          const distance = Math.sqrt(
            Math.pow(node.x - otherNode.x, 2) + Math.pow(node.y - otherNode.y, 2)
          );

          // Connect core nodes to everything nearby, regular nodes only to very close nodes
          const maxDistance = node.isCore || otherNode.isCore ? 200 : 100;

          if (distance < maxDistance) {
            ctx.strokeStyle = alpha(superAdminTheme.accent, (1 - distance / maxDistance) * 0.2);
            ctx.lineWidth = node.isCore || otherNode.isCore ? 2 : 1;
            ctx.beginPath();
            ctx.moveTo(node.x, node.y);
            ctx.lineTo(otherNode.x, otherNode.y);
            ctx.stroke();

            // Create data particles between connected nodes
            if (Math.random() > 0.98 && particles.length < 50) {
              particles.push(new DataParticle(node, otherNode));
            }
          }
        });
      });

      // Update and draw particles
      particles.forEach((particle, index) => {
        particle.update();
        particle.draw();

        // Remove old particles
        if (Math.random() > 0.99) {
          particles.splice(index, 1);
        }
      });

      // Draw nodes on top
      nodes.forEach((node) => node.draw());

      requestAnimationFrame(animate);
    };

    animate();

    const handleResize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const handleChange = (field) => (event) => {
    const value = event.target.value;
    setFormData((prev) => ({ ...prev, [field]: value }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.username || !formData.password) {
      setError('Authentication credentials required');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const result = await signIn({
        username: formData.username,
        password: formData.password,
      });

      if (result.success) {
        navigate('/super-admin', { replace: true });
      } else {
        setError(result.error || 'Authentication failed');
      }
    } catch (err) {
      setError(err.message || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  // Terminal-style text field styling
  const textFieldStyles = {
    mb: 3,
    '& .MuiOutlinedInput-root': {
      backgroundColor: alpha(superAdminTheme.dark, 0.8),
      backdropFilter: 'blur(20px)',
      transition: 'all 0.3s ease',
      borderRadius: 1,
      fontFamily: 'monospace',
      '& fieldset': {
        borderColor: alpha(superAdminTheme.accent, 0.2),
        borderWidth: '1px',
      },
      '&:hover': {
        backgroundColor: alpha(superAdminTheme.dark, 0.9),
        boxShadow: `
          0 0 20px ${alpha(superAdminTheme.accent, 0.1)},
          inset 0 0 10px ${alpha(superAdminTheme.accent, 0.05)}
        `,
        '& fieldset': {
          borderColor: alpha(superAdminTheme.accent, 0.4),
        },
      },
      '&.Mui-focused': {
        backgroundColor: superAdminTheme.dark,
        boxShadow: `
          0 0 30px ${alpha(superAdminTheme.accent, 0.2)},
          inset 0 0 20px ${alpha(superAdminTheme.accent, 0.05)}
        `,
        '& fieldset': {
          borderColor: superAdminTheme.accent,
          borderWidth: '2px',
        },
      },
    },
    '& .MuiInputLabel-root': {
      color: alpha(superAdminTheme.light, 0.6),
      fontFamily: 'monospace',
      fontSize: '0.875rem',
      '&.Mui-focused': {
        color: superAdminTheme.accent,
      },
    },
    '& .MuiInputBase-input': {
      fontFamily: 'monospace',
      letterSpacing: '0.05em',
    },
  };

  return (
    <Box
      ref={containerRef}
      sx={{
        minHeight: '100vh',
        background: superAdminTheme.gradient1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Subtle Particle Canvas */}
      <canvas
        ref={canvasRef}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          opacity: 0.3,
        }}
      />

      {/* Authority Grid Pattern */}
      <Box
        sx={{
          position: 'absolute',
          width: '100%',
          height: '100%',
          backgroundImage: `
            radial-gradient(circle at 20% 50%, ${alpha(superAdminTheme.accent, 0.1)} 0%, transparent 50%),
            radial-gradient(circle at 80% 80%, ${alpha(superAdminTheme.terminal, 0.08)} 0%, transparent 50%),
            radial-gradient(circle at 40% 20%, ${alpha(superAdminTheme.warning, 0.05)} 0%, transparent 50%)
          `,
          animation: 'authority-pulse 20s ease-in-out infinite',
        }}
      />

      {/* Developer Grid */}
      <Box
        sx={{
          position: 'absolute',
          width: '100%',
          height: '100%',
          backgroundImage: `
            linear-gradient(${alpha(superAdminTheme.accent, 0.03)} 1px, transparent 1px),
            linear-gradient(90deg, ${alpha(superAdminTheme.accent, 0.03)} 1px, transparent 1px)
          `,
          backgroundSize: '100px 100px',
          animation: 'grid-shift 30s linear infinite',
        }}
      />

      {/* Animated Code Blocks */}
      <Box
        sx={{
          position: 'absolute',
          width: '200%',
          height: '200%',
          background: superAdminTheme.gradient2,
          animation: 'code-float 40s ease-in-out infinite',
          filter: 'blur(100px)',
          transform: 'translate(-25%, -25%)',
        }}
      />

      {/* Security Icons Background */}
      <Box
        sx={{
          position: 'absolute',
          width: '100%',
          height: '100%',
          pointerEvents: 'none',
        }}
      ></Box>

      <Fade in timeout={600}>
        <Paper
          elevation={0}
          sx={{
            width: '100%',
            maxWidth: 480,
            borderRadius: 2,
            backgroundColor: alpha(superAdminTheme.secondary, 0.95),
            backdropFilter: 'blur(20px)',
            position: 'relative',
            overflow: 'hidden',
            zIndex: 1,
            border: `1px solid ${alpha(superAdminTheme.accent, 0.3)}`,
            boxShadow: `
              0 0 50px ${alpha(superAdminTheme.accent, 0.1)},
              0 20px 40px ${alpha(superAdminTheme.dark, 0.8)},
              inset 0 0 30px ${alpha(superAdminTheme.dark, 0.5)}
            `,
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '2px',
              background: superAdminTheme.accent,
              animation: 'terminal-glow 2s ease-in-out infinite',
              boxShadow: `0 0 20px ${superAdminTheme.accent}`,
              pointerEvents: 'none',
            },
          }}
        >
          {/* Developer Terminal Header */}
          <Box
            sx={{
              background: superAdminTheme.dark,
              p: 4,
              textAlign: 'center',
              position: 'relative',
              overflow: 'hidden',
              borderBottom: `2px solid ${alpha(superAdminTheme.accent, 0.2)}`,
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: '-100%',
                width: '200%',
                height: '100%',
                background: `linear-gradient(90deg, transparent, ${alpha(superAdminTheme.accent, 0.1)}, transparent)`,
                animation: 'scan-line 6s linear infinite',
              },
              '&::after': {
                content: '""',
                position: 'absolute',
                bottom: 0,
                left: 0,
                right: 0,
                height: '1px',
                background: `linear-gradient(90deg, transparent, ${superAdminTheme.accent}, transparent)`,
                animation: 'scan-line 3s linear infinite',
              },
            }}
          >
            {/* FleetCam Logo */}
            <Box sx={{ mb: 3 }}>
              <img
                src="/images/FC Logo 2.png"
                alt="FleetCam Logo"
                style={{
                  height: 70,
                  opacity: 0.9,
                }}
              />
            </Box>

            <Typography
              variant="h3"
              sx={{
                fontWeight: 900,
                letterSpacing: 1,
                color: 'white',
                mb: 1,
                fontFamily: 'monospace',
                textShadow: `
                  0 0 20px ${alpha(superAdminTheme.accent, 0.8)},
                  0 0 40px ${alpha(superAdminTheme.accent, 0.4)}
                `,
                position: 'relative',
                zIndex: 1,
              }}
            >
              SuperUser
            </Typography>
            <Typography
              variant="subtitle1"
              sx={{
                color: alpha(superAdminTheme.light, 0.8),
                fontWeight: 500,
                letterSpacing: 0.5,
                fontSize: '0.9rem',
                fontFamily: 'monospace',
                position: 'relative',
                zIndex: 1,
              }}
            >
              Super Admin Console Access
            </Typography>

            {/* Developer Mode Indicators */}
            <Box sx={{ mt: 2, display: 'flex', gap: 1, justifyContent: 'center' }}>
              <Chip
                size="small"
                icon={<VpnKey sx={{ fontSize: 16 }} />}
                label="ROOT ACCESS"
                sx={{
                  backgroundColor: alpha(superAdminTheme.terminal, 0.15),
                  color: superAdminTheme.terminal,
                  fontWeight: 700,
                  fontSize: '0.7rem',
                  letterSpacing: 0.5,
                  fontFamily: 'monospace',
                  border: `1px solid ${alpha(superAdminTheme.terminal, 0.3)}`,
                }}
              />
            </Box>
          </Box>

          {/* Form Section */}
          <Box sx={{ p: 5 }}>
            {/* Error Alert */}
            {(error || authError) && (
              <Alert
                severity="error"
                sx={{
                  mb: 3,
                  backgroundColor: alpha(theme.palette.error.main, 0.1),
                  border: `2px solid ${theme.palette.error.main}`,
                  '& .MuiAlert-message': {
                    fontWeight: 600,
                    letterSpacing: 0.5,
                  },
                }}
              >
                {error || authError}
              </Alert>
            )}

            {/* Login Form */}
            <form onSubmit={handleSubmit}>
              <TextField
                fullWidth
                label="Username"
                name="username"
                value={formData.username}
                onChange={handleChange('username')}
                disabled={loading || isLoading}
                autoComplete="off"
                sx={textFieldStyles}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <VerifiedUser sx={{ color: superAdminTheme.accent, fontSize: 22 }} />
                    </InputAdornment>
                  ),
                }}
              />

              <TextField
                fullWidth
                label="Password"
                name="password"
                type={showPassword ? 'text' : 'password'}
                value={formData.password}
                onChange={handleChange('password')}
                disabled={loading || isLoading}
                autoComplete="off"
                sx={textFieldStyles}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <VpnKey sx={{ color: superAdminTheme.accent, fontSize: 22 }} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                        disabled={loading || isLoading}
                        sx={{
                          color: superAdminTheme.accent,
                          '&:hover': {
                            backgroundColor: alpha(superAdminTheme.accent, 0.1),
                          },
                        }}
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <Button
                fullWidth
                type="submit"
                variant="contained"
                size="large"
                disabled={loading || isLoading || !formData.username || !formData.password}
                sx={{
                  py: 2.5,
                  fontSize: '1.2rem',
                  fontWeight: 900,
                  background: `linear-gradient(135deg, ${superAdminTheme.primary} 0%, ${superAdminTheme.secondary} 50%, ${superAdminTheme.accent} 100%)`,
                  backgroundSize: '200% 200%',
                  animation: 'gradient-sweep 3s ease infinite',
                  color: 'white',
                  borderRadius: 2,
                  textTransform: 'uppercase',
                  letterSpacing: 2,
                  boxShadow: `
                    0 10px 40px ${alpha(superAdminTheme.primary, 0.5)},
                    inset 0 2px 4px ${alpha(superAdminTheme.light, 0.2)}
                  `,
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  position: 'relative',
                  overflow: 'hidden',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: '-100%',
                    width: '100%',
                    height: '100%',
                    background: `linear-gradient(90deg, transparent, ${alpha(superAdminTheme.light, 0.4)}, transparent)`,
                    transition: 'left 0.5s ease',
                  },
                  '&:hover': {
                    background: alpha(superAdminTheme.accent, 0.1),
                    boxShadow: `
                      0 0 30px ${alpha(superAdminTheme.accent, 0.5)},
                      inset 0 0 30px ${alpha(superAdminTheme.accent, 0.15)}
                    `,
                    '&::before': {
                      left: '100%',
                    },
                  },
                  '&:active': {
                    transform: 'translateY(-1px) scale(1.01)',
                  },
                  '&:disabled': {
                    background: alpha(superAdminTheme.primary, 0.5),
                    animation: 'none',
                  },
                }}
              >
                {loading || isLoading ? (
                  <CircularProgress size={26} sx={{ color: 'white' }} />
                ) : (
                  <Box
                    sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'center' }}
                  >
                    <Lock sx={{ fontSize: 20 }} />
                    Authenticate
                  </Box>
                )}
              </Button>
            </form>

            {/* Footer */}
            <Box sx={{ mt: 4, textAlign: 'center' }}>
              <Typography
                variant="caption"
                sx={{
                  color: alpha(theme.palette.text.secondary, 0.5),
                  display: 'block',
                  fontSize: '0.7rem',
                  letterSpacing: 1,
                  mb: 1,
                }}
              >
                All access attempts are monitored and logged
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: alpha(superAdminTheme.accent, 0.6),
                  fontSize: '0.65rem',
                  fontWeight: 700,
                  mb: 2,
                }}
              >
                Unauthorized access is strictly prohibited
              </Typography>

              {/* Copyright and Version */}
              <Box
                sx={{
                  pt: 2,
                  borderTop: `1px solid ${alpha(superAdminTheme.accent, 0.1)}`,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    color: alpha(superAdminTheme.light, 0.3),
                    fontSize: '0.65rem',
                    letterSpacing: '0.02em',
                  }}
                >
                  © FleetCam Pty Ltd 2025
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: alpha(superAdminTheme.light, 0.3),
                    fontSize: '0.65rem',
                    fontFamily: 'monospace',
                  }}
                >
                  v1.0.0
                </Typography>
              </Box>
            </Box>
          </Box>
        </Paper>
      </Fade>

      {/* Advanced animations */}
      <style>
        {`
          @keyframes gentle-float {
            0%, 100% { 
              transform: translateY(0px) scale(1);
            }
            50% { 
              transform: translateY(-30px) scale(1.05);
            }
          }
          
          @keyframes pulse-subtle {
            0%, 100% { 
              transform: scale(1);
              opacity: 0.05;
            }
            50% { 
              transform: scale(1.1);
              opacity: 0.08;
            }
          }
          
          @keyframes levitate {
            0%, 100% { transform: translateY(0px); }
            50% { transform: translateY(-20px); }
          }
          
          @keyframes expand-ring {
            0% {
              transform: translate(-50%, -50%) scale(1);
              opacity: 1;
            }
            100% {
              transform: translate(-50%, -50%) scale(1.5);
              opacity: 0;
            }
          }
          
          @keyframes scan-line {
            0% { left: -100%; }
            100% { left: 100%; }
          }
          
          @keyframes authority-pulse {
            0%, 100% { 
              transform: scale(1) rotate(0deg);
              opacity: 0.3;
            }
            50% { 
              transform: scale(1.1) rotate(180deg);
              opacity: 0.5;
            }
          }
          
          @keyframes grid-shift {
            0% { transform: translate(0, 0); }
            100% { transform: translate(100px, 100px); }
          }
          
          @keyframes code-float {
            0%, 100% { transform: translate(-25%, -25%) scale(1); }
            50% { transform: translate(-25%, -25%) scale(1.1); }
          }
          
          
          @keyframes terminal-glow {
            0%, 100% { opacity: 0.8; }
            50% { opacity: 1; }
          }
          
          @keyframes pulse-glow {
            0%, 100% { 
              transform: scale(1);
              box-shadow: 0 0 10px rgba(245, 158, 11, 0.3);
            }
            50% { 
              transform: scale(1.05);
              box-shadow: 0 0 20px rgba(245, 158, 11, 0.5);
            }
          }
          
          @keyframes gradient-sweep {
            0% { background-position: 0% 50%; }
            50% { background-position: 100% 50%; }
            100% { background-position: 0% 50%; }
          }
          
          @keyframes blink {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.6; }
          }
          
        `}
      </style>
    </Box>
  );
};

export default SuperAdminLogin;
